import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { PropPropertyHeader } from "@app/integrations/urm/models/prop-property-header";
import { ResponseWrapper } from "@app/integrations/urm/models/response-wrapper";
import { ClientConfigService } from "@app/core/config/client-config.service";

const SIMULATED_ADDRESSES = [
  {
    name: "14 Birchwood Crescent",
    addr1: "14 Birchwood Crescent",
    addr2: "Oakfield Estate",
    addr3: "Swindon",
    addr4: "Wiltshire",
    postCode: "SN3 4PQ"
  },
  {
    name: "Flat 7, Hayward Court",
    addr1: "Flat 7, Hayward Court",
    addr2: "Wellington Road",
    addr3: "Bristol",
    addr4: "",
    postCode: "BS2 9DN"
  },
  {
    name: "22 Meadow Lane",
    addr1: "22 Meadow Lane",
    addr2: "Eastville",
    addr3: "Gloucester",
    addr4: "Gloucestershire",
    postCode: "GL1 5RT"
  },
  {
    name: "3 The Willows",
    addr1: "3 The Willows",
    addr2: "Bridgwater Road",
    addr3: "Taunton",
    addr4: "Somerset",
    postCode: "TA1 2HE"
  }
];

@Injectable({
  providedIn: "root"
})
export class PropertyService {
  private clientConfigService = inject(ClientConfigService);
  private http = inject(HttpClient);

  private static PATHS = {
    GetPropertyHeadersByIdentity:
      "/Services/UniclassRIA-Web-Services-Property-Details.svc/JSON/GetPropertyByIdentity",
    GetPropertyHeadersById:
      "/Services/UniclassRIA-Web-Services-Property-Details.svc/JSON/GetPropertyById",
    GetPropertyHeadersByFilter:
      "/Services/UniclassRIA-Web-Services-Property-Details.svc/JSON/GetPropertyHeadersByFilterPaged"
  };

  async loadPropertyByIdentity(id: number): Promise<PropPropertyHeader> {
    if (this.clientConfigService.config.urmServices.simulate) {
      return this.simulateProperty(id.toString());
    }
    const config = await this.clientConfigService.config;
    return new Promise<PropPropertyHeader>((resolve, reject) => {
      const options = { params: { propertyId: id.toString(10) } };
      this.http
        .get(
          config.urmServices.serviceUrl +
            PropertyService.PATHS.GetPropertyHeadersByIdentity,
          options
        )
        .subscribe(
          (value: any) => {
            let resultWrapper =
              value.GetPropertyByIdentityResult as ResponseWrapper<PropPropertyHeader>;
            if (resultWrapper.RootResults.length === 0) {
              reject("Property Record not found.");
            } else if (resultWrapper.RootResults.length > 1) {
              reject("Critical Error: Multiple properties matched this ID.");
            }
            resolve(resultWrapper.RootResults[0]);
          },
          (error1) => {
            reject(error1);
          }
        );
    });
  }

  async loadPropertyByFilter(
    filter: string,
    page: number,
    pageSize: number
  ): Promise<ResponseWrapper<PropPropertyHeader>> {
    if (this.clientConfigService.config.urmServices.simulate) {
      const wrapper = new ResponseWrapper<PropPropertyHeader>();
      wrapper.RootResults = SIMULATED_ADDRESSES.map((a, i) =>
        this.buildProperty(String(i + 1), a)
      );
      wrapper.TotalCount = wrapper.RootResults.length;
      return Promise.resolve(wrapper);
    }
    const config = await this.clientConfigService.config;
    return new Promise<ResponseWrapper<PropPropertyHeader>>(
      (resolve, reject) => {
        const options = {
          withCredentials: true
        };
        this.http
          .post(
            config.urmServices.serviceUrl +
              PropertyService.PATHS.GetPropertyHeadersByFilter,
            { propertyFilter: filter, pageIndex: page, pageSize: pageSize },
            options
          )
          .subscribe(
            (value: any) => {
              let resultWrapper =
                value.GetPropertyHeadersByFilterPagedResult as ResponseWrapper<PropPropertyHeader>;
              resolve(resultWrapper);
            },
            (error1) => {
              reject(error1);
            }
          );
      }
    );
  }

  async loadPropertyById(id: string): Promise<PropPropertyHeader> {
    if (this.clientConfigService.config.urmServices.simulate) {
      return this.simulateProperty(id);
    }
    const config = await this.clientConfigService.config;
    return new Promise<PropPropertyHeader>((resolve, reject) => {
      const options = { params: { propertyId: id } };
      this.http
        .get(
          config.urmServices.serviceUrl +
            PropertyService.PATHS.GetPropertyHeadersById,
          options
        )
        .subscribe(
          (value: any) => {
            let resultWrapper =
              value.GetPropertyByIdResult as ResponseWrapper<PropPropertyHeader>;
            if (resultWrapper.RootResults.length === 0) {
              reject("Property Record not found.");
            } else if (resultWrapper.RootResults.length > 1) {
              reject("Critical Error: Multiple properties matched this ID.");
            }
            resolve(resultWrapper.RootResults[0]);
          },
          (error1) => {
            reject(error1);
          }
        );
    });
  }

  private simulateProperty(id: string): Promise<PropPropertyHeader> {
    const index = Math.abs(this.hashCode(id)) % SIMULATED_ADDRESSES.length;
    const addr = SIMULATED_ADDRESSES[index];
    return Promise.resolve(this.buildProperty(id, addr));
  }

  private buildProperty(
    id: string,
    addr: (typeof SIMULATED_ADDRESSES)[0]
  ): PropPropertyHeader {
    const prop = new PropPropertyHeader();
    prop.Proh_PropertyIdentity = Math.abs(this.hashCode(id));
    prop.Proh_PropertyId = id;
    prop.Proh_CommonRefNo = "CR-" + id;
    prop.Proh_PropertyTypeID = "RES";
    prop.Proh_PropertyName = addr.name;
    prop.Proh_Address1 = addr.addr2;
    prop.Proh_Address2 = addr.addr3;
    prop.Proh_Address3 = addr.addr4;
    prop.Proh_Address4 = "";
    prop.Proh_PostCode = addr.postCode;
    return prop;
  }

  private hashCode(s: string): number {
    let hash = 0;
    for (let i = 0; i < s.length; i++) {
      hash = (Math.imul(31, hash) + s.charCodeAt(i)) | 0;
    }
    return hash;
  }
}
